import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useMe } from '../hooks';
import { Loading, PageHead } from '../ui';

// Read the token once and remove it from the address bar right away (it must not stay in history or be synced)
function takeToken() {
  const token = new URLSearchParams(window.location.hash.slice(1)).get('t') || '';
  if (token) history.replaceState(history.state, '', window.location.pathname + window.location.search);
  return token;
}

/** Magic link from the e-mail: logs in right away and opens the Sportplaner. */
export default function LoginLink() {
  // the token is only accepted from the URL fragment (#t=…): it never reaches server or proxy logs
  const [token] = useState(takeToken);
  const me = useMe();
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(token ? '' : 'Der Link ist unvollständig. Bitte einen neuen Code anfordern.');
  const qc = useQueryClient();
  const nav = useNavigate();
  const started = useRef(false);
  // Someone is already signed in in this browser: ask before switching (a link from someone else's mail
  // must not silently swap the account)
  const ask = !!me.data && !confirmed;
  useEffect(() => {
    if (!token || me.isLoading || ask || started.current) return; // only once (React may run effects twice in dev)
    started.current = true;
    api('/auth/link', { method: 'POST', body: { token } })
      .then(async () => {
        await qc.invalidateQueries();
        void nav('/', { replace: true });
      })
      .catch((e: Error) => setError(e.message));
  }, [token, me.isLoading, ask, qc, nav]);
  if (token && me.isLoading) return <Loading />;
  return (
    <div style={{ maxWidth: 520 }}>
      <PageHead title="Anmelden" />
      <div className="panel form">
        {error ? (
          <>
            <p className="err" role="alert">
              {error}
            </p>
            <div className="form-actions">
              <Link className="btn btn-primary" to="/login">
                Neuen Code anfordern
              </Link>
            </div>
          </>
        ) : ask ? (
          <>
            <p className="note">
              Du bist in diesem Browser schon als{' '}
              <b>
                {me.data!.first_name} {me.data!.last_name}
              </b>{' '}
              angemeldet.
            </p>
            <div className="form-actions">
              <Link className="btn btn-ghost" to="/" replace>
                Weiter als {me.data!.first_name}
              </Link>
              <button className="btn btn-primary" onClick={() => setConfirmed(true)}>
                Mit diesem Link anmelden
              </button>
            </div>
          </>
        ) : (
          <p className="note" aria-live="polite">
            Du wirst angemeldet …
          </p>
        )}
      </div>
    </div>
  );
}
